/**
 * Broken glass on the ground: the crumbs of a bus shelter's panes (see
 * `core/vehicle/shards.ts`), drawn as one instanced mesh of small, flat,
 * glittering pieces. A crumb at rest is written once and left.
 */

import { BoxGeometry, DynamicDrawUsage, Euler, InstancedMesh, Matrix4, MeshStandardMaterial, Quaternion, Vector3 } from 'three';
import type { ShardGround } from '../core/vehicle/shards.js';
import { ShardField } from '../core/vehicle/shards.js';

/** Most crumbs lying about at once: about four shelters' worth. */
const MOST = 2000;

const matrix = new Matrix4();
const at = new Vector3();
const turn = new Quaternion();
const euler = new Euler();
const scale = new Vector3();

export class Shards {
  readonly mesh: InstancedMesh;
  readonly field = new ShardField(MOST);
  private readonly moved = new Uint8Array(MOST);

  constructor() {
    const material = new MeshStandardMaterial({
      color: 0xd5ebf2, roughness: 0.12, metalness: 0.45, flatShading: true, transparent: true, opacity: 0.9,
    });
    this.mesh = new InstancedMesh(new BoxGeometry(1, 0.4, 0.8), material, MOST);
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
  }

  /** A crumb at (x, y, z) flying at `velocity`, `size` metres across, tumbling at `spin` rad/s. */
  add(x: number, y: number, z: number, velocity: [number, number, number], size: number, spin: number): void {
    const i = this.field.add(x, y, z, velocity[0], velocity[1], velocity[2], size, spin);
    this.write(i);
    this.mesh.count = this.field.count;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Move the crumbs on by `dt` over `ground`; `shift` is how far the scene's origin moved. */
  update(dt: number, ground: ShardGround, shift: { x: number; z: number }): void {
    const field = this.field;
    if (field.count === 0) return;
    const moved = shift.x !== 0 || shift.z !== 0;
    if (moved) field.shift(shift.x, shift.z);
    field.step(dt, ground, this.moved);
    let any = moved;
    for (let i = 0; i < field.count; i++) {
      if (!moved && this.moved[i] === 0) continue;
      this.write(i);
      any = true;
    }
    if (any) this.mesh.instanceMatrix.needsUpdate = true;
  }

  private write(i: number): void {
    const f = this.field;
    at.set(f.p[i * 3], f.p[i * 3 + 1] + f.size[i] * 0.2, f.p[i * 3 + 2]);
    turn.setFromEuler(euler.set(f.turn[i * 3], f.turn[i * 3 + 1], f.turn[i * 3 + 2]));
    scale.setScalar(f.size[i]);
    this.mesh.setMatrixAt(i, matrix.compose(at, turn, scale));
  }
}
